import type {
  ActivityExecutionMode,
  ActivityHandoffProjection,
  ActivityHandoffState,
  ActivitySource,
  ActivityStatus,
  ActivityWorkPath,
  ControlPlaneOperationalProjection,
  IssueStateCategory,
  Phase,
  PhaseConformitySummary,
  ProviderId,
  RbacAction,
  Role,
} from '@internal/spassu-code-contract';
import {
  DEFAULT_PIPELINE_DEFINITION,
  getPhaseLabels,
  getPhaseOrder,
} from '@internal/spassu-code-contract';

export type {
  ActivityExecutionMode,
  ActivityHandoffProjection,
  ActivityHandoffState,
  ActivitySource,
  ActivityStatus,
  ActivityWorkPath,
  // Multi-provider SCM/Boards types (Tarefa 5+)
  Issue,
  IssueQuery,
  IssueScope,
  IssueSearchResult,
  IssueStateCategory,
  Phase,
  PhaseTransitionRule,
  PipelineDefinition,
  PipelinePhaseDefinition,
  ProviderId,
  Repository,
  RepositoryBranch,
  RepositoryHierarchy,
  RepositoryOwner,
  RepositoryProject,
  SkillOrigin,
  SkillRegistrationType,
} from '@internal/spassu-code-contract';

/**
 * Provider descriptor surfaced by `GET /scm/providers`. Drives the frontend's
 * adaptive picker — when only one is enabled we render it silently.
 */
export interface ScmProviderDescriptor {
  id: import('@internal/spassu-code-contract').ProviderId;
  displayName: string;
  hierarchy: import('@internal/spassu-code-contract').RepositoryHierarchy;
}

/**
 * Projeto gerenciável na tela "Gestão de Projetos". Cada item equivale a um
 * `project_key` no esquema RBAC agnóstico (= owner do provider). Para Azure
 * DevOps, esse owner é a organização configurada; para GitHub, a org/usuário.
 */
export interface ManagedProject {
  /** project_key usado em todas as APIs RBAC. */
  name: string;
  /** Provider de origem — usado para agrupar/filtrar a UI. */
  providerId: import('@internal/spassu-code-contract').ProviderId;
  providerLabel: string;
}

export type ActivityDeliveryOutcome = 'delivered' | 'closed_without_delivery';

export type ActivityHealthState =
  | 'healthy'
  | 'projecting'
  | 'degraded'
  | 'blocked'
  | 'recovery-needed';

export type ActivityHealthActionCode =
  | 'none'
  | 'run_doctor'
  | 'reconnect_backend'
  | 'retry_sync'
  | 'inspect_projection';

export interface ActivityHealthSignal {
  state: ActivityHealthState;
  reasonCode: string;
  summary: string;
  details?: Record<string, unknown>;
}

export interface ActivityCurrentHealth {
  checkpoint: string;
  localHealth: ActivityHealthSignal;
  remoteHealth: ActivityHealthSignal;
  overallHealth: ActivityHealthSignal;
  actionCode: ActivityHealthActionCode;
  actionText: string | null;
  emittedAt: string;
}

export interface ActivityWorkItem {
  id: number;
  type: string;
  url: string;
  title: string;
  state: string;
}

export interface ActivityIssue {
  externalId: string;
  type: string;
  title: string;
  state: string;
  stateCategory: IssueStateCategory | null;
  url: string;
}

export interface Activity {
  id: string;
  userId: string;
  title: string;
  description: string;
  /** @deprecated Use `repoProject` (will be null for providers without project layer). */
  azureProject: string;
  /** @deprecated Use `repoName`. */
  azureRepository: string;
  branch: string | null;
  clonePath: string | null;
  currentPhase: Phase;
  status: ActivityStatus;
  errorMessage?: string | null;
  executionMode: ActivityExecutionMode;
  source?: ActivitySource;
  workPath?: ActivityWorkPath;
  handoffState?: ActivityHandoffState;
  handoff?: ActivityHandoffProjection;
  commitsJson?: string | null;
  prUrl?: string | null;
  deliveryOutcome?: ActivityDeliveryOutcome | null;
  /** @deprecated Use `issue`. */
  azureWorkItem?: ActivityWorkItem | null;
  /** Provider-agnostic identification (multi-provider). */
  provider?: string;
  providerExternalId?: string | null;
  repoOwner?: string | null;
  repoProject?: string | null;
  repoName?: string | null;
  issue?: ActivityIssue | null;
  currentHealth?: ActivityCurrentHealth | null;
  /** Evidence summary consumed by the existing CTA in a later rollout. */
  phaseConformity?: PhaseConformitySummary | null;
  createdAt: string;
  updatedAt: string;
  finalizedAt?: string | null;
  /**
   * Canonical Control Plane projection, attached by the portfolio list in one
   * batched read. Absent for non-Control-Plane activities.
   */
  controlPlane?: ControlPlaneOperationalProjection;
}

export interface AzureProject {
  id: string;
  name: string;
  description?: string;
}

export interface AzureRepository {
  id: string;
  name: string;
  defaultBranch?: string;
  remoteUrl?: string;
  webUrl?: string;
}

export interface CreateActivityInput {
  title: string;
  description?: string;
  /** @deprecated Use `repoProject` (set explicitly to `null` for providers without project layer). */
  azureProject?: string;
  /** @deprecated Use `repoName`. */
  azureRepository?: string;
  branch?: string;
  workPath: ActivityWorkPath;
  // Azure Boards Work Item (optional — absent means "created from scratch")
  /** @deprecated Use `issueExternalId`. */
  azureWorkItemId?: number;
  /** @deprecated Use `issueType`. */
  azureWorkItemType?: string;
  /** @deprecated Use `issueUrl`. */
  azureWorkItemUrl?: string;
  /** @deprecated Use `issueTitle`. */
  azureWorkItemTitle?: string;
  /** @deprecated Use `issueState`. */
  azureWorkItemState?: string;
  // Provider-agnostic shape (multi-provider). When `provider` is anything
  // other than 'azure-devops' the agnostic fields are required.
  provider?: ProviderId;
  repoOwner?: string;
  repoProject?: string | null;
  repoName?: string;
  issueExternalId?: string;
  issueType?: string;
  issueTitle?: string;
  issueState?: string;
  issueUrl?: string;
}

export interface AzureBranch {
  name: string;
  isDefault: boolean;
}

export interface ActivityFilters {
  status?: ActivityStatus;
  workPath?: ActivityWorkPath;
  azureProject?: string;
  azureRepository?: string;
  source?: string;
  issueExternalId?: string;
  createdBy?: string;
  createdFrom?: string;
  createdToExclusive?: string;
  page?: number;
  limit?: number;
  mineOnly?: boolean;
}

export interface ActivityFilterOptions {
  projects: string[];
  repositories: string[];
  creators: string[];
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export const PHASES_ORDER: Phase[] = getPhaseOrder(DEFAULT_PIPELINE_DEFINITION);
export const PHASE_LABELS: Record<Phase, string> = getPhaseLabels(
  DEFAULT_PIPELINE_DEFINITION,
);

export const STATUS_LABELS: Record<ActivityStatus, string> = {
  preparing: 'Preparando',
  active: 'Ativa',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  error: 'Erro',
};

/**
 * Reexportado do contrato compartilhado: a cópia local desta interface omitia
 * `capabilities` e não tinha verificação de equivalência com o servidor.
 */

// ─── Skill Vault Types ───

export interface SkillSummary {
  id: string;
  name: string;
  version: string | null;
  description: string | null;
  phase: string | null;
  content: string;
  signature: string | null;
  signedBy: string | null;
  signedAt: string | null;
  status: string;
  origin: string;
  reviewerId: string | null;
  approvedAt: string | null;
  usageCount: number;
  avgQualityScore: number | null;
  avgTokensSaved: number | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ─── RBAC Types ───

/**
 * Vocabulário RBAC do contrato compartilhado. O import nomeado existe porque os
 * dois tipos continuam em uso neste arquivo, e o reexport preserva quem já
 * importa daqui.
 */
export type { RbacAction, Role };

export interface ProjectMember {
  id: string;
  projectKey: string;
  repositoryKey: string | null;
  entityRef: string;
  entityKind: 'User' | 'Group';
  role: Role;
  assignedBy: string;
  createdAt: string;
  expiresAt: string | null;
}

export interface AccessibleProject {
  projectKey: string;
  roles: Role[];
  repositories: string[];
  allRepositories: boolean;
  canSeeAllActivities: boolean;
}

export interface AuditLogEntry {
  id: string;
  actorRef: string;
  action: string;
  projectKey: string | null;
  resourceType: string | null;
  resourceId: string | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  createdAt: string;
}

export interface AuditLogFilters {
  projectKey?: string;
  action?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  gestor: 'Gestor de Projeto',
  analista: 'Analista de Requisitos',
  desenvolvedor: 'Desenvolvedor',
  revisor: 'Revisor',
  aprovador: 'Aprovador',
  observador: 'Observador',
};

export interface ComplianceMetrics {
  projectKey: string;
  period: { days: number; since: string };
  activities: {
    total: number;
    active: number;
    completed: number;
    errors: number;
  };
  artifacts: { total: number; approved: number; pending: number };
  auditActions: number;
  members: { total: number; temporary: number; expired: number };
  policies: Record<string, unknown>;
}

// ─── Azure Boards — Work Item types ───

export interface AzureWorkItemType {
  name: string;
  referenceName: string;
  description?: string;
  color?: string;
  icon?: string;
  isDisabled?: boolean;
  states?: Array<{ name: string; category: string }>;
}

export interface AzureWorkItemSummary {
  id: number;
  type: string;
  title: string;
  state: string;
  assignedTo?: string;
  areaPath?: string;
  iterationPath?: string;
  tags?: string[];
  url: string;
  parentId?: number;
}

export interface AzureWorkItemDetail extends AzureWorkItemSummary {
  description?: string;
  descriptionText?: string;
  acceptanceCriteria?: string;
  acceptanceCriteriaText?: string;
  reproSteps?: string;
  reproStepsText?: string;
  rev: number;
  parent?: { id: number; type: string; title: string };
}

export interface SearchWorkItemsFilters {
  projectId: string;
  types?: string[];
  states?: string[];
  assignedToMe?: boolean;
  assignedToRef?: string;
  text?: string;
  workItemId?: number;
  areaPath?: string;
  iterationPath?: string;
  top?: number;
  skip?: number;
}

export interface PaginatedWorkItems {
  data: AzureWorkItemSummary[];
  total: number;
  top: number;
  skip: number;
  hasMore: boolean;
}
